import React, { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react';
import { debounce } from 'lodash';
import { Box, Popper, styled, Typography, Button, SxProps, IconButton } from '@mui/material';
import { fontFamilies } from '@cfra-nextgen-frontend/shared/src/utils/fonts';
import { useClickableAreas } from '@cfra-nextgen-frontend/shared/src/hooks/useClickableAreas';
import { ReactComponent as CloseIcon } from '@cfra-nextgen-frontend/shared/src/assets/images/CloseIcon_2.svg';
import { CustomSvgIcon } from '@cfra-nextgen-frontend/shared/src/components/Icon/CustomSvgIcon';
import { GetOptionsComponentProps } from '../TypeSearch/TypeSearch';
import { SuggesterResultsContext } from './SuggesterResultsContext';
import { ResearchDescriptionText } from '../TypeSearch/styledComponents';

export interface PopularSearch {
    id: string;
    text: string;
}

export interface ResearchItem {
    id: string;
    date: string;
    author: string;
    type: string;
    title: string;
    tooltip?: string;
}

export interface SymbolItem {
    id: string;
    symbol: string;
    companyName: string;
    ratingType?: 'STARS' | 'Quant' | 'ETF' | 'MF';
    ratingValue?: string | number;
    priceTarget?: string;
    category?: string;
}

export interface SmartSearchProps {
    showSearchInput: boolean;
    onClickOutsideCallback?: () => void;
    onEmptyInputClearCallback?: () => void;
    onAskCFRAiClick?: (query?: string) => void;
    onResearchHubClick?: (query?: string) => void;
    onSymbolClick?: (symbol: SymbolItem) => void;
    onResearchClick?: (research: ResearchItem) => void;
    onPopularSearchClick?: (search: PopularSearch) => void;
    popularSearches?: PopularSearch[];
    placeholder?: string;
    isLoading?: boolean;
    onInputChange?: (inputValue: string) => void;
    topLinkParams?: {
        text: string;
        pathname: string;
        navigateTo?: string;
    };
    getLeftOptions: (props: GetOptionsComponentProps) => JSX.Element;
    getRightOptions: (props: GetOptionsComponentProps) => JSX.Element;
    AIResponseViewComponent?: React.ComponentType<{
        initialQuery?: string;
        onBack: () => void;
        onClose: () => void;
        initialQAPairs?: any[];
        onConversationChange?: (pairs: any[]) => void;
    }>;
}

const SearchContainer = styled(Box)(({ theme }) => ({
    background: '#f8f9fa',
    border: '2px solid #007bff',
    borderRadius: '25px 25px 0 0',
    boxShadow: '0 2px 8px rgba(0, 123, 255, 0.15)',
    width: '100%',
    maxWidth: '710px',
    padding: '12px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const SearchInput = styled('input')(({ theme }) => ({
    flex: 1,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontSize: '16px',
    color: '#495057',
    fontWeight: 500,
    fontFamily: fontFamilies.GraphikRegular,
    '&::placeholder': {
        color: '#6c757d',
    },
}));

const ClearIconButton = styled(IconButton)(({ theme }) => ({
    padding: 0,
    width: '20px',
    height: '20px',
    '&:hover': {
        backgroundColor: 'transparent',
    },
}));

const AskCFRAiButton = styled(Button)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    background: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    fontFamily: fontFamilies.GraphikRegular,
    '&:hover': {
        background: '#002b5a',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 123, 255, 0.3)',
    },
}));

export function AskCFRAiButtonComponent({
    onClick,
    sx,
}: {
    onClick: React.MouseEventHandler<HTMLButtonElement>;
    sx?: SxProps;
}) {
    return (
        <AskCFRAiButton onClick={onClick} sx={sx}>
            Ask CFRAi
        </AskCFRAiButton>
    );
}

const SearchResultsDropdown = styled(Box)(({ theme }) => ({
    background: 'white',
    border: '2px solid #007bff',
    borderTop: 'none',
    borderRadius: '0 0 20px 20px',
    boxShadow: '0 4px 12px rgba(0, 123, 255, 0.15)',
    width: '710px',
    overflow: 'hidden',
    position: 'relative',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
    maxHeight: 'calc(90vh - 75px)',
    overflowY: 'auto',
    overflowX: 'hidden',
    borderRadius: '0 0 18px 18px',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '12px',
    fontSize: '14px',
    color: '#495057',
}));

const PopularSearchItem = styled(Box)(({ theme }) => ({
    margin: '4px 0',
    color: '#3c3c3c',
    fontSize: '14px',
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    fontFamily: fontFamilies.GraphikRegular,
    '&:hover': {
        color: '#007ab9',
        backgroundColor: '#f3f5fc',
        transform: 'translateX(4px)',
    },
}));

const LinkButton = styled(Box)(({ theme }) => ({
    color: '#007ab9',
    textDecoration: 'underline',
    fontSize: '14px',
    fontWeight: 'normal',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: fontFamilies.GraphikRegular,
    padding: '2px 6px',
    marginTop: '-2px',
    marginRight: '-6px',
    '&:hover': {
        backgroundColor: '#f3f5fc',
        borderRadius: '3px',
    },
}));

const PrimarySearchButton = styled(Button)(({ theme }) => ({
    background: '#f8f9fa',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    border: 'none',
    width: '100%',
    fontFamily: fontFamilies.GraphikRegular,
    textTransform: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: 'rgba(0, 123, 255, 0.05)',
        transform: 'translateX(4px)',
    },
}));

function GoToResearchHubButton({ onClick, onCloseSearch }: { onClick: () => void; onCloseSearch: () => void }) {
    const { researchSuggesterResultsNumber } = useContext(SuggesterResultsContext);

    if (!researchSuggesterResultsNumber) {
        return null;
    }

    const handleClick = () => {
        onCloseSearch();
        onClick();
    };

    return (
        <Button
            onClick={handleClick}
            sx={{
                textTransform: 'none',
                color: '#007ab9',
                textDecoration: 'underline',
                fontSize: '14px',
                fontWeight: 'normal',
                minWidth: 'auto',
                transition: 'all 0.2s ease',
                fontFamily: fontFamilies.GraphikRegular,
                padding: '2px 6px',
                borderRadius: '3px',
                marginTop: '-2px',
                marginRight: '-6px',
                '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: '#f3f5fc',
                },
            }}>
            Go to Research Hub
        </Button>
    );
}

function SymbolsSection({ leftOptionsComponent }: { leftOptionsComponent: React.ReactNode }) {
    const { companySuggesterResultsNumber } = useContext(SuggesterResultsContext);

    return (
        <Box
            sx={{
                padding: '16px 20px',
                borderBottom: '1px solid #e9ecef',
                display: companySuggesterResultsNumber === 0 ? 'none' : 'block',
                'button:not(:last-child)::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: '8px',
                    right: '8px',
                    height: '1.2px',
                    background: 'rgba(0, 0, 0, 0.08)',
                },
            }}>
            <Typography
                variant='body2'
                sx={{
                    fontFamily: fontFamilies.GraphikMedium,
                    marginBottom: '12px',
                    color: '#002b5a',
                    fontSize: '16px',
                }}>
                Symbols
            </Typography>
            {leftOptionsComponent}
        </Box>
    );
}

function ResearchSection({
    rightOptionsComponent,
    handleResearchHubClick,
    onCloseSearch,
}: {
    rightOptionsComponent: React.ReactNode;
    handleResearchHubClick: () => void;
    onCloseSearch: () => void;
}) {
    const { researchSuggesterResultsNumber } = useContext(SuggesterResultsContext);

    return (
        <Box
            sx={{
                padding: '16px 20px',
                'a:not(:last-child)': { borderBottom: '1.2px solid rgba(0, 0, 0, 0.12)' },
                display: researchSuggesterResultsNumber === 0 ? 'none' : 'block',
            }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                }}>
                <Typography
                    variant='body2'
                    sx={{
                        fontFamily: fontFamilies.GraphikMedium,
                        color: '#002b5a',
                        fontSize: '16px',
                    }}>
                    Research
                </Typography>
                <GoToResearchHubButton onClick={handleResearchHubClick} onCloseSearch={onCloseSearch} />
            </Box>
            {rightOptionsComponent}
        </Box>
    );
}

function NoResultsSection({ inputValue }: { inputValue: string }) {
    const { researchSuggesterResultsNumber, companySuggesterResultsNumber } = useContext(SuggesterResultsContext);

    // Show only when both research and symbols results are 0
    const shouldShow = researchSuggesterResultsNumber === 0 && companySuggesterResultsNumber === 0;

    if (!shouldShow) {
        return null;
    }

    return (
        <Box
            sx={{
                padding: '20px',
                textAlign: 'center',
            }}>
            <ResearchDescriptionText textAlign={'left'}>No results found</ResearchDescriptionText>
        </Box>
    );
}

export function SmartSearch({
    showSearchInput,
    onClickOutsideCallback,
    onEmptyInputClearCallback,
    onAskCFRAiClick,
    onResearchHubClick,
    onSymbolClick,
    onResearchClick,
    onPopularSearchClick,
    popularSearches = [],
    placeholder,
    getLeftOptions,
    getRightOptions,
    onInputChange,
    AIResponseViewComponent,
}: SmartSearchProps) {
    const [inputValue, setInputValue] = useState<string>('');
    const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null);
    const [isPopperReady, setIsPopperReady] = useState(false);
    const [showAIView, setShowAIView] = useState(false);
    const [aiQuery, setAiQuery] = useState<string | undefined>(undefined);
    const [aiConversations, setAiConversations] = useState<Record<string, any[]>>({});

    const normalizeQuery = useCallback((q?: string) => (q || '').trim().replace(/\s+/g, ' ').toLowerCase(), []);
    const initialPairsForQuery = React.useMemo(
        () => aiConversations[normalizeQuery(aiQuery)],
        [aiConversations, aiQuery, normalizeQuery],
    );
    const onAIConversationChange = useCallback(
        (pairs: any[]) => {
            const key = normalizeQuery(aiQuery);
            if (!key) return;
            setAiConversations((prev) => {
                if (prev[key] === pairs) return prev; // avoid unnecessary state updates
                return { ...prev, [key]: pairs };
            });
        },
        [aiQuery, normalizeQuery],
    );

    const searchContainerRef = useRef<HTMLDivElement | null>(null);
    const leftOptionsOuterContainerRef = useRef<HTMLDivElement | null>(null);
    const searchResultsDropdownRef = useRef<HTMLDivElement | null>(null);
    const resetOptionsRef = useRef<any>(null);
    const smartSearchContainerRef = useRef<HTMLDivElement | null>(null);
    const anchorElementRef = useRef<HTMLDivElement | null>(null);

    const handleClickOutside = useCallback(() => {
        if (!showSearchInput) {
            return;
        }
        // Don't close on outside click when AI view is showing
        if (showAIView) {
            return;
        }
        onClickOutsideCallback?.();
    }, [showSearchInput, showAIView, onClickOutsideCallback]);

    const { clickableAreasRefs } = useClickableAreas<HTMLInputElement | HTMLDivElement>({
        clickOutsideCallback: handleClickOutside,
        numberOfAreas: 2,
    });

    const setSearchAreaRef = useCallback(
        (el: HTMLDivElement | null) => {
            clickableAreasRefs.current[0] = el;
            searchContainerRef.current = el;
        },
        [clickableAreasRefs],
    );

    const setResultsAreaRef = useCallback(
        (el: HTMLDivElement | null) => {
            clickableAreasRefs.current[1] = el;
        },
        [clickableAreasRefs],
    );

    useEffect(() => {
        if (!showSearchInput || !inputNode) {
            return;
        }
        // Focus input when expanded
        inputNode.focus();
    }, [showSearchInput, inputNode]);

    // Prevent background scrolling when SmartSearch is open
    useEffect(() => {
        const handleWheel = (e: WheelEvent) => {
            // Check if the event target is within SmartSearch container
            const container = smartSearchContainerRef.current;
            if (container && container.contains(e.target as Node)) {
                e.preventDefault();
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            // Check if the event target is within SmartSearch container
            const container = smartSearchContainerRef.current;
            if (container && container.contains(e.target as Node)) {
                e.preventDefault();
            }
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            // Prevent arrow keys and page up/down from scrolling when SmartSearch is open
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
                e.preventDefault();
            }
        };

        if (showSearchInput) {
            // Add event listeners to document to catch all scroll events
            document.addEventListener('wheel', handleWheel, { passive: false });
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('keydown', handleKeyDown, { passive: false });

            // Also prevent scrolling by setting body overflow
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('wheel', handleWheel);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [showSearchInput]);

    const debouncedAnalyticsHandler = useMemo(
        () =>
            debounce((newValue: string) => {
                onInputChange?.(newValue);
            }, 200),
        [onInputChange],
    );

    const handleInputChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            // Immediately update input value for UI responsiveness
            const newValue = event.target.value;
            setInputValue(newValue);
            // Use debounced handler for analytics only
            debouncedAnalyticsHandler(newValue);
        },
        [debouncedAnalyticsHandler],
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedAnalyticsHandler.cancel();
        };
    }, [debouncedAnalyticsHandler]);

    // Clear search term when the search input collapses
    useEffect(() => {
        if (!showSearchInput) {
            if (inputValue !== '') {
                setInputValue('');
                debouncedAnalyticsHandler.cancel();
                onInputChange?.('');
                onEmptyInputClearCallback?.();
            }
            // Ensure AI view state is reset when collapsing
            if (showAIView) {
                setShowAIView(false);
                setAiQuery(undefined);
            }
        }
    }, [showSearchInput, inputValue, debouncedAnalyticsHandler, onInputChange, onEmptyInputClearCallback, showAIView]);

    const handleClearClick = useCallback(() => {
        setInputValue('');
        debouncedAnalyticsHandler.cancel();
        onInputChange?.('');
        if (inputValue === '') {
            onEmptyInputClearCallback?.();
        }
        inputNode?.focus();
    }, [inputValue, onEmptyInputClearCallback, inputNode, debouncedAnalyticsHandler, onInputChange]);

    const handleAskCFRAiClick = useCallback(() => {
        setAiQuery(inputValue || undefined);
        setShowAIView(true);
    }, [inputValue]);

    const handlePopularSearchClick = useCallback(
        (search: PopularSearch) => {
            setInputValue(search.text);
            onPopularSearchClick?.(search);
        },
        [onPopularSearchClick],
    );

    const setLeftOptionsOuterContainerRef = useCallback(
        (node?: HTMLDivElement | null) => {
            if (node === undefined) {
                return;
            }
            leftOptionsOuterContainerRef.current = node;
        },
        [leftOptionsOuterContainerRef],
    );

    const leftOptionsComponent = getLeftOptions
        ? getLeftOptions({
              inputValue: inputValue,
              resetOptionsRef,
              onLinkClickCallback: onClickOutsideCallback,
              outerContainerRef: leftOptionsOuterContainerRef,
              titleWithScrollableAreaProps: {
                  containerSx: {
                      width: '100%',
                      maxWidth: '100%',
                  },
                  scrollableAreaContainerWrapperSx: {
                      width: '100%',
                      maxWidth: '100%',
                  },
                  scrollableAreaContainerSx: {
                      padding: '0px',
                  },
                  setOuterContainerRef: setLeftOptionsOuterContainerRef,
              },
          })
        : null;

    const rightOptionsComponent = getRightOptions
        ? getRightOptions({
              inputValue: inputValue,
              resetOptionsRef,
              onLinkClickCallback: onClickOutsideCallback,
              outerContainerRef: searchResultsDropdownRef,
              setThirdAreaRef: (el: HTMLDivElement | null) => {
                  clickableAreasRefs.current[1] = el;
              },
              titleWithScrollableAreaProps: {
                  containerSx: {
                      width: '100%',
                      maxWidth: '100%',
                  },
                  scrollableAreaContainerWrapperSx: {
                      width: '100%',
                      maxWidth: '100%',
                  },
                  scrollableAreaContainerSx: {
                      padding: '0px',
                  },
                  setOuterContainerRef: () => {},
              },
          })
        : null;

    // Show sections based on search results - only show when we have actual input and results
    const shouldShowSymbols = Boolean(inputValue && isPopperReady && leftOptionsComponent);
    const shouldShowResearch = Boolean(inputValue && isPopperReady && rightOptionsComponent);

    if (!showSearchInput) {
        return null;
    }

    return (
        <Box ref={smartSearchContainerRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: '710px' }}>
                {/* Anchor wrapper - always present for stable Popper positioning */}
                <Box
                    ref={(el: HTMLDivElement | null) => {
                        anchorElementRef.current = el;
                        if (el && !isPopperReady) {
                            setIsPopperReady(true);
                        }
                    }}
                    sx={{
                        width: '100%',
                        minHeight: showAIView ? '58px' : 0, // Reserve space when SearchContainer is hidden
                    }}>
                    {/* Search Container - hide when AI view is showing */}
                    <SearchContainer
                        ref={setSearchAreaRef}
                        sx={{
                            display: showAIView ? 'none' : 'flex',
                        }}>
                        <Box
                            sx={{
                                width: '20px',
                                height: '20px',
                                border: '2px solid #6c757d',
                                borderRadius: '50%',
                                position: 'relative',
                            }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    width: '8px',
                                    height: '2px',
                                    background: '#6c757d',
                                    transform: 'rotate(45deg)',
                                    right: '-6px',
                                    bottom: '-6px',
                                }}
                            />
                        </Box>
                        <SearchInput
                            ref={(node) => setInputNode(node)}
                            value={inputValue}
                            onChange={handleInputChange}
                            placeholder={placeholder}
                        />
                        {inputValue && (
                            <ClearIconButton onClick={handleClearClick}>
                                <CustomSvgIcon
                                    component={CloseIcon}
                                    viewBox='0 0 18 18'
                                    sx={{
                                        width: '18px',
                                        fill: '#AAAAAA',
                                        '&:hover': {
                                            fill: '#007AB9',
                                        },
                                    }}
                                />
                            </ClearIconButton>
                        )}
                        <Box sx={{ width: '1px', height: '24px', background: '#dee2e6' }} />
                        <AskCFRAiButtonComponent
                            onClick={() => {
                                onClickOutsideCallback?.();
                                onAskCFRAiClick?.(inputValue || undefined);
                            }}
                        />
                    </SearchContainer>
                </Box>

                {/* Search Results Dropdown */}
                {isPopperReady && anchorElementRef.current && (
                    <Popper
                        ref={setResultsAreaRef}
                        open={true}
                        anchorEl={anchorElementRef.current}
                        placement='bottom-start'
                        sx={{
                            zIndex: 3000,
                            width: '100%',
                            maxWidth: '710px',
                            overflow: 'visible', // Allow content to expand for infinite scroll
                        }}
                        modifiers={[
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 0],
                                },
                            },
                            {
                                name: 'preventOverflow',
                                options: {
                                    boundary: 'viewport',
                                    padding: 8,
                                },
                            },
                            {
                                name: 'flip',
                                options: {
                                    fallbackPlacements: ['bottom-start', 'top-start'],
                                },
                            },
                        ]}>
                        {showAIView && AIResponseViewComponent ? (
                            <Box sx={{ marginTop: '-58px' }}>
                                <AIResponseViewComponent
                                    initialQuery={aiQuery}
                                    initialQAPairs={initialPairsForQuery}
                                    onConversationChange={onAIConversationChange}
                                    onBack={() => {
                                        setShowAIView(false);
                                    }}
                                    onClose={() => {
                                        setShowAIView(false);
                                        setAiQuery(undefined);
                                        onClickOutsideCallback?.();
                                    }}
                                />
                            </Box>
                        ) : (
                            <SearchResultsDropdown>
                                <ScrollableContent ref={searchResultsDropdownRef}>
                                    {/* Ask CFRAi Section */}
                                    <Box sx={{ padding: '16px 20px', borderBottom: '1px solid #e9ecef' }}>
                                        <SectionHeader>
                                            <Box
                                                sx={{
                                                    width: '12px',
                                                    height: '12px',
                                                    background: 'white',
                                                    borderRadius: '3px',
                                                    position: 'relative',
                                                }}>
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        top: '50%',
                                                        left: '50%',
                                                        transform: 'translate(-50%, -50%)',
                                                        width: '6px',
                                                        height: '6px',
                                                        background: '#007bff',
                                                        borderRadius: '2px',
                                                    }}
                                                />
                                            </Box>
                                            <span
                                                style={{
                                                    color: '#002b5a',
                                                    fontSize: '16px',
                                                    fontFamily: fontFamilies.GraphikRegular,
                                                }}>
                                                Ask CFRAi
                                            </span>
                                            <Box
                                                sx={{
                                                    background: '#6c757d',
                                                    color: 'white',
                                                    fontSize: '10px',
                                                    padding: '2px 6px',
                                                    borderRadius: '4px',
                                                    fontWeight: 400,
                                                    marginLeft: '4px',
                                                    fontFamily: fontFamilies.GraphikMedium,
                                                }}>
                                                BETA
                                            </Box>
                                            <LinkButton sx={{ marginLeft: 'auto' }}>More Popular</LinkButton>
                                        </SectionHeader>

                                        <PrimarySearchButton onClick={handleAskCFRAiClick}>
                                            <span style={{ fontWeight: 600, color: '#007ab9' }}>
                                                {inputValue || 'Search CFRA content'}
                                            </span>
                                            <span style={{ fontSize: '18px', color: '#007ab9' }}>+</span>
                                        </PrimarySearchButton>

                                        <Box
                                            sx={{
                                                fontWeight: 400,
                                                marginBottom: '8px',
                                                color: '#002b5a',
                                                fontSize: '16px',
                                                fontFamily: fontFamilies.GraphikMedium,
                                            }}>
                                            Popular Searches:
                                        </Box>
                                        {popularSearches.slice(0, 2).map((search) => (
                                            <PopularSearchItem
                                                key={search.id}
                                                onClick={() => handlePopularSearchClick(search)}>
                                                {search.text}
                                            </PopularSearchItem>
                                        ))}
                                    </Box>

                                    {/* Symbols Section */}
                                    {shouldShowSymbols && (
                                        <SymbolsSection leftOptionsComponent={leftOptionsComponent} />
                                    )}

                                    {/* Research Section */}
                                    {shouldShowResearch && (
                                        <ResearchSection
                                            rightOptionsComponent={rightOptionsComponent}
                                            handleResearchHubClick={() => onResearchHubClick?.(inputValue)}
                                            onCloseSearch={onClickOutsideCallback || (() => {})}
                                        />
                                    )}

                                    {/* No Results Section */}
                                    {inputValue && <NoResultsSection inputValue={inputValue} />}
                                </ScrollableContent>
                            </SearchResultsDropdown>
                        )}
                    </Popper>
                )}
            </Box>
        </Box>
    );
}
